import React, { useMemo } from 'react';

import {
  AppointmentPreview,
  AppointmentPreviewFields,
} from '@work4all/components/lib/components/entity-preview/appointment-preview';
import { useEntityPreviewData } from '@work4all/components/lib/components/entity-preview/hooks/use-entity-preview-data';
import { EntityPreviewContainerProps } from '@work4all/components/lib/components/entity-preview/types';

import { Appointment } from '@work4all/models/lib/Classes/Appointment.entity';
import { EMailTemplateKind } from '@work4all/models/lib/Enums/EMailTemplateKind.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { useAppointmentEmailTemplate } from '../../../../../../components/data-tables/appointments-table/hooks/use-appointment-email-template';
import { useGetAppoinmentEmailsParams } from '../../../../../../components/data-tables/appointments-table/hooks/use-get-appoinment-emails-params';
import { EntityPreviewDataProviderProps } from '../../../../../../components/data-tables/entity-table/types';
import { mergeActions } from '../../../../../../components/data-tables/entity-table/utils';

type IAppointmentPreviewContainerProps = EntityPreviewContainerProps & {
  entries?: { id?: string | number }[];
};

export function AppointmentPreviewContainer(
  props: IAppointmentPreviewContainerProps
) {
  return (
    <AppointmentPreview {...props} entries={props.entries as Appointment[]} />
  );
}

export const AppointmentPreviewDataProvider = (
  props: EntityPreviewDataProviderProps & {
    children: (props: EntityPreviewDataProviderProps) => React.ReactNode;
  }
) => {
  const { children, ...dataProps } = props;

  const { entries, ...others } = dataProps;
  const ids = entries.map((e) => e.id);

  // Extract ids from entries if ids is not provided
  const appointmentIds =
    ids ||
    (entries
      ?.map((entry) => entry.id)
      .filter((id) => id !== undefined) as number[]) ||
    [];
  const appointments = useEntityPreviewData<Appointment>(
    appointmentIds as number[],
    Entities.appointment,
    AppointmentPreviewFields
  );

  const getEmailParams = useGetAppoinmentEmailsParams(appointments?.[0]);
  const action = useAppointmentEmailTemplate({
    getEmailParams,
    eMailTemplateKind: EMailTemplateKind.APPOINTMENT_NOTIFICATION,
    noTemplate: true,
    appointments,
  });

  const custom = useMemo(
    () =>
      mergeActions(
        {
          right: [action],
        },
        props.custom
      ),
    [props.custom, action]
  );

  if (!appointments) {
    return null;
  }

  return (
    <>
      {children({
        ...others,
        entries: appointments,
        custom,
      })}
    </>
  );
};
